import { supabase } from '../lib/supabaseClient';
import React, { useRef, useEffect, useState } from 'react';
import logo from '../assets/logo.jpg';

// Add timestamp to bust browser cache for updated videos
const CACHE_BUST = Date.now();

export default function Hero() {
  const [videoSrc, setVideoSrc] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const videoRef = useRef(null);
  const [videoEnded, setVideoEnded] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [churchPhone, setChurchPhone] = useState('+91 73393 26116');
  const [phoneEnabled, setPhoneEnabled] = useState(true);

  // Load phone from Supabase church_settings
  useEffect(() => {
    supabase.from('church_settings').select('key,value')
      .then(({ data }) => {
        if (data) {
          const m = {};
          data.forEach(r => { m[r.key] = r.value; });
          if (m.church_phone) setChurchPhone(m.church_phone);
          if (m.church_phone_enabled) setPhoneEnabled(m.church_phone_enabled !== 'false');
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    // Detect mobile
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);

    // Load video URL from Supabase with cache busting
    const filename = mobile ? 'hero-video-mobile.mp4' : 'hero-video.mp4';
    const desktopUrl = supabase.storage.from('gallery').getPublicUrl('hero-video.mp4').data.publicUrl;
    const mobileUrl = supabase.storage.from('gallery').getPublicUrl('hero-video-mobile.mp4').data.publicUrl;

    // Use mobile video if available, else fallback to desktop
    const url = `${mobile ? mobileUrl : desktopUrl}?v=${CACHE_BUST}`;
    setVideoSrc(url);
  }, []);

  // When videoSrc is set, load and play the video
  useEffect(() => {
    if (videoSrc && videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [videoSrc]);

  const [skipCount, setSkipCount] = useState(null);

  const handleVideoEnd = () => {
    setFadeOut(true);
    setTimeout(() => setVideoEnded(true), 1000);
  };

  return (
    <>
      <style>{`
        @keyframes logoFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%,100%{opacity:0.6} 50%{opacity:1} }
        @keyframes pulseGold { 0%,100%{box-shadow:0 0 50px rgba(201,168,76,0.5),0 0 100px rgba(201,168,76,0.15)} 50%{box-shadow:0 0 70px rgba(201,168,76,0.7),0 0 140px rgba(201,168,76,0.25)} }

        .hero-content-wrap > * { animation: fadeInUp 1s ease forwards; opacity: 0; }
        .hero-content-wrap > *:nth-child(1) { animation-delay: 0.2s; }
        .hero-content-wrap > *:nth-child(2) { animation-delay: 0.4s; }
        .hero-content-wrap > *:nth-child(3) { animation-delay: 0.6s; }
        .hero-content-wrap > *:nth-child(4) { animation-delay: 0.8s; }
        .hero-content-wrap > *:nth-child(5) { animation-delay: 1.0s; }
        .hero-content-wrap > *:nth-child(6) { animation-delay: 1.2s; }
        .hero-content-wrap > *:nth-child(7) { animation-delay: 1.4s; }

        .hero-btn-primary:hover { transform:translateY(-3px); box-shadow:0 14px 38px rgba(201,168,76,0.6) !important; }
        .hero-btn-outline:hover  { background:rgba(201,168,76,0.15) !important; transform:translateY(-3px); }

        .intro-video-wrap {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 1s ease;
        }
        .intro-video-wrap.fading { opacity: 0; pointer-events: none; }

        .skip-btn {
          position: absolute;
          bottom: 36px;
          right: 36px;
          background: rgba(201,168,76,0.15);
          border: 2px solid rgba(201,168,76,0.8);
          color: var(--primary);
          font-family: 'Raleway', sans-serif;
          font-size: 0.82rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          padding: 12px 26px;
          border-radius: 40px;
          cursor: pointer;
          transition: all 0.25s;
          z-index: 10000;
          text-transform: uppercase;
          backdrop-filter: blur(8px);
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .skip-btn:hover {
          background: rgba(201,168,76,0.35);
          border-color: var(--primary);
          transform: scale(1.05);
        }
      `}</style>

      {/* ── FULLSCREEN INTRO VIDEO (plays once) ── */}
      {!videoEnded && (
        <div className={`intro-video-wrap${fadeOut ? ' fading' : ''}`}>
          <video
            ref={videoRef}
            src={videoSrc || '/hero-video.mp4'}
            muted
            playsInline
            onEnded={handleVideoEnd}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />

          {/* Church name overlay on video */}
          <div style={{
            position: 'absolute',
            bottom: 0, left: 0, right: 0,
            padding: '60px 40px 80px',
            background: 'linear-gradient(to top, rgba(7,15,34,0.85) 0%, transparent 100%)',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: "'Cinzel Decorative', serif",
              fontSize: 'clamp(0.6rem, 1.5vw, 0.8rem)',
              color: 'var(--primary)',
              letterSpacing: '0.4em',
              marginBottom: 10,
            }}>
              ✦ Jesus Is Lord ✦
            </div>
            <div style={{
              fontFamily: "'Cinzel Decorative', serif",
              fontSize: 'clamp(1.5rem, 4vw, 3rem)',
              color: 'var(--text)',
              textShadow: '0 2px 20px rgba(0,0,0,0.9)',
              lineHeight: 1.2,
            }}>
              ZION <span style={{ color: 'var(--primary)' }}>Assemblies of God</span>
            </div>
          </div>

          {/* Skip button */}
          <button className="skip-btn" onClick={handleVideoEnd}>
            Skip ›
          </button>
        </div>
      )}

      {/* ── HERO SECTION (shows after video ends) ── */}
      <section
        id="home"
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #030a18 0%, var(--bg-dark) 45%, #1a0808 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: 64,
          paddingBottom: 20,
        }}
      >
        {/* Background glow */}
        <div style={{
          position: 'absolute',
          width: 700, height: 700, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        {/* Big cross bg */}
        <div style={{
          position: 'absolute',
          fontSize: '50vw', opacity: 0.03,
          color: 'var(--primary)', userSelect: 'none',
          pointerEvents: 'none', lineHeight: 1,
        }}>✝</div>

        {/* Content */}
        <div
          className="hero-content-wrap"
          style={{
            position: 'relative', zIndex: 2,
            textAlign: 'center', padding: '20px 20px',
            maxWidth: 780,
          }}
        >
          {/* Logo */}
          <div style={{
            width: 140, height: 100,
            borderRadius: '50% / 45%',
            overflow: 'hidden',
            border: '4px solid var(--primary)',
            animation: 'logoFloat 6s ease-in-out infinite, pulseGold 4s ease-in-out infinite',
            margin: '0 auto 18px',
            background: 'var(--bg-dark)',
          }}>
            <img src={logo} alt="Zion AG" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          </div>

          <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: 'clamp(0.6rem,1.8vw,.85rem)', color: 'var(--primary)', letterSpacing: '.45em', textTransform: 'uppercase', marginBottom: 8 }}>
            ✦ Jesus Is Lord ✦
          </div>

          <h1 style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: 'clamp(2rem,6vw,4.2rem)', color: 'var(--text)', lineHeight: 1.1, marginBottom: 6, textShadow: '0 0 70px rgba(201,168,76,0.3)' }}>
            ZION<br /><span style={{ color: 'var(--primary)' }}>Assemblies of God</span>
          </h1>

          <div style={{ fontFamily: "'Crimson Pro', serif", fontSize: 'clamp(1rem,2.5vw,1.3rem)', color: 'var(--text-muted)', letterSpacing: '.2em', marginBottom: 16 }}>
            Church · Ooty, Tamil Nadu
          </div>

          <blockquote style={{ fontFamily: "'Crimson Pro', serif", fontStyle: 'italic', fontSize: 'clamp(.95rem,2.2vw,1.15rem)', color: 'var(--primary-light)', margin: '0 auto 14px', maxWidth: 520, padding: '12px 18px', borderLeft: '3px solid var(--primary)', background: 'rgba(201,168,76,0.05)', borderRadius: '0 10px 10px 0', textAlign: 'left' }}>
            "For God so loved the world that He gave His one and only Son…"
            <br /><span style={{ fontSize: '.85em', opacity: .8 }}>— John 3:16</span>
          </blockquote>

          <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', letterSpacing: '.05em', lineHeight: 1.8, marginTop: 10, marginBottom: 20 }}>
            📍 Near Gel Memorial School, Church Road, Fernhill, Ooty – 643004<br />
            {phoneEnabled && (
              <span style={{ fontSize: '0.8rem' }}>📞 {churchPhone}</span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="#services" className="hero-btn-primary" style={{ display: 'inline-block', background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))', color: 'var(--bg-deep)', fontFamily: "'Raleway',sans-serif", fontWeight: 700, fontSize: '.82rem', letterSpacing: '.12em', textTransform: 'uppercase', padding: '14px 34px', borderRadius: 40, textDecoration: 'none', boxShadow: '0 8px 28px rgba(201,168,76,0.4)', transition: 'all .25s' }}>
              Join Us Sunday
            </a>
            <a href="#prayer" className="hero-btn-outline" style={{ display: 'inline-block', border: '2px solid var(--primary)', color: 'var(--primary)', fontFamily: "'Raleway',sans-serif", fontWeight: 700, fontSize: '.82rem', letterSpacing: '.12em', textTransform: 'uppercase', padding: '12px 30px', borderRadius: 40, textDecoration: 'none', background: 'transparent', transition: 'all .25s' }}>
              Submit Prayer Request
            </a>
          </div>

          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'rgba(201,168,76,0.6)', fontSize: '.65rem', letterSpacing: '.2em', textTransform: 'uppercase', animation: 'shimmer 2s ease-in-out infinite' }}>
            <span>Scroll Down</span>
            <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom, rgba(201,168,76,0.6), transparent)' }} />
          </div>
        </div>
      </section>
    </>
  );
}
